import { AdminLayout, useSource } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { useNavigate, useParams } from "@opendash/router";
import { AdminToolbar } from "@opendash/ui";
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Spin,
  Tabs,
  Tag,
  Timeline,
  TimelineItemProps,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import { useEffect, useState } from "react";
import {
  DeliveryNoteDetailDrawer,
  DeliveryNotesTable,
} from "../features/deliveryNotes";
import {
  GoodsReceiptProcessPositionsDrawer,
  GoodsReceiptProcessTable,
} from "../features/goodsReceiptProcess";
import {
  GoodsReceivingItemsDrawer,
  GoodsReceivingTable,
} from "../features/goodsReceiving";
import { PackageDetailDrawer, PackagesTable } from "../features/packages";
import { getUsername } from "../helper/getUsername";
import {
  SIV_DeliveryNotes,
  SIV_GoodsReceiptProcess,
  SIV_GoodsReceiving,
  SIV_Order,
  SIV_Package,
} from "../parsegenerated";

type Props = {};

export const OrderDetailPage = observer((props: Props) => {
  const [order, setOrder] = useState<SIV_Order | null>(null);
  const [goodsReceipts, setGoodsReceipts] = useState<SIV_GoodsReceiptProcess[]>(
    [],
  );
  const [goodsReceivings, setGoodsReceivings] = useState<SIV_GoodsReceiving[]>(
    [],
  );
  const [packages, setPackages] = useState<SIV_Package[]>([]);
  const [deliveryNotes, setDeliveryNotes] = useState<SIV_DeliveryNotes[]>([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentSource, setSource] = useSource();

  type NavItem = { id: string; no: string };

  const [navList, setNavList] = useState<NavItem[]>(() => {
    try {
      const raw = sessionStorage.getItem("siv-orders-nav-list");
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown[];
      if (parsed.length === 0) return [];
      // Support both old format (string[]) and new format ({id,no}[])
      if (typeof parsed[0] === "string") return [];
      return parsed as NavItem[];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!id || navList.some((item) => item.id === id)) return;

    let storedSortField: string | undefined;
    let storedSortOrder: string | undefined;
    try {
      const raw = localStorage.getItem("siv-orders-overview-sort");
      if (raw) {
        const parsed = JSON.parse(raw) as {
          sortField?: unknown;
          sortOrder?: unknown;
        };
        if (
          (parsed.sortField === "startedAt" ||
            parsed.sortField === "appointedAt" ||
            parsed.sortField === "finishedAt") &&
          (parsed.sortOrder === "ascend" || parsed.sortOrder === "descend")
        ) {
          storedSortField = parsed.sortField;
          storedSortOrder = parsed.sortOrder;
        }
      }
    } catch {}

    const NAV_PAGE_SIZE = 100;
    const fetchAllItems = async (): Promise<NavItem[]> => {
      const items: NavItem[] = [];
      let page = 1;
      while (true) {
        const res = (await Parse.Cloud.run("siv-get-orders", {
          page,
          pageSize: NAV_PAGE_SIZE,
          sortField: storedSortField,
          sortOrder: storedSortOrder,
          currentSelectedSource: currentSource?.id,
        })) as { results: SIV_Order[]; count: number };
        items.push(
          ...res.results.map((r) => ({ id: r.id as string, no: r.no })),
        );
        if (items.length >= res.count || res.results.length < NAV_PAGE_SIZE)
          break;
        page++;
      }
      return items;
    };

    fetchAllItems()
      .then((items) => {
        try {
          sessionStorage.setItem("siv-orders-nav-list", JSON.stringify(items));
        } catch {}
        setNavList(items);
      })
      .catch(() => {});
  }, [id]);

  const currentNavIndex = navList.findIndex((item) => item.id === (id || ""));
  const prevOrder = currentNavIndex > 0 ? navList[currentNavIndex - 1] : null;
  const nextOrder =
    currentNavIndex >= 0 && currentNavIndex < navList.length - 1
      ? navList[currentNavIndex + 1]
      : null;

  // Drawer states
  const [selectedGoodsReceiving, setSelectedGoodsReceiving] =
    useState<SIV_GoodsReceiving | null>(null);
  const [goodsReceivingDetailsOpen, setGoodsReceivingDetailsOpen] =
    useState(false);
  const [selectedGoodsReceipt, setSelectedGoodsReceipt] =
    useState<SIV_GoodsReceiptProcess | null>(null);
  const [goodsReceiptDetailsOpen, setGoodsReceiptDetailsOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<SIV_Package | null>(
    null,
  );
  const [packageDetailsOpen, setPackageDetailsOpen] = useState(false);
  const [selectedDeliveryNote, setSelectedDeliveryNote] =
    useState<SIV_DeliveryNotes | null>(null);
  const [deliveryNoteDetailsOpen, setDeliveryNoteDetailsOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchOrder = async () => {
      setLoading(true);
      try {
        // Fetch order
        const orderQuery = new Parse.Query(SIV_Order);
        orderQuery.include("processor");
        orderQuery.include("responsible");
        orderQuery.include("tenant");
        // @ts-ignore
        orderQuery.include("tenant.dataSource");
        const orderResult = await orderQuery.get(id);
        setOrder(orderResult);
        if (
          orderResult.get("tenant") &&
          orderResult.get("tenant")!.get("dataSource") &&
          orderResult.get("tenant")!.get("dataSource")!.id
        ) {
          setSource(orderResult.get("tenant")?.get("dataSource")!.id!);
        }

        // Fetch GoodsReceipts for this order
        const receiptQuery = new Parse.Query(SIV_GoodsReceiptProcess);
        receiptQuery.equalTo("order", orderResult);
        receiptQuery.include("deliveryNote");
        receiptQuery.include("tenant");
        receiptQuery.descending("date");
        const receipts = await receiptQuery.find();
        setGoodsReceipts(receipts);

        // Fetch GoodsReceivings for this order
        const receivingQuery = new Parse.Query(SIV_GoodsReceiving);
        receivingQuery.equalTo("order", orderResult);
        receivingQuery.include("deliveryNote");
        receivingQuery.include("tenant");
        receivingQuery.descending("date");
        const receivings = await receivingQuery.find();
        setGoodsReceivings(receivings);

        // Fetch Packages for this order
        const packageQuery = new Parse.Query(SIV_Package);
        packageQuery.equalTo("order", orderResult);
        packageQuery.include("deliveryNote");
        packageQuery.include("tenant");
        packageQuery.descending("packagingDate");
        const pkgs = await packageQuery.find();
        setPackages(pkgs);

        // Fetch DeliveryNotes for this order
        const deliveryNoteQuery = new Parse.Query(SIV_DeliveryNotes);
        deliveryNoteQuery.equalTo("order", orderResult);
        deliveryNoteQuery.include("tenant");
        deliveryNoteQuery.descending("date");
        const deliveryNts = await deliveryNoteQuery.find();
        setDeliveryNotes(deliveryNts);
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const getTimelineItems = (): TimelineItemProps[] => {
    if (!order) return [];

    const items: TimelineItemProps[] = [];

    // Started
    if (order.startedAt) {
      items.push({
        color: "green",
        dot: <Icon icon="fa:play-circle" />,
        children: (
          <>
            <Typography.Text strong>Gestartet</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {dayjs(order.startedAt).format("DD.MM.YYYY HH:mm")}
            </Typography.Text>
          </>
        ),
      });
    }

    // GoodsReceipts and their related GoodsReceivings
    goodsReceipts.forEach((receipt) => {
      // Find all receivings for this receipt
      const relatedReceivings = goodsReceivings.filter(
        (receiving) => receiving.goodsReceiptProcess?.id === receipt.id,
      );

      items.push({
        color: "purple",
        dot: <Icon icon="fa:truck" />,
        children: (
          <>
            <Typography.Text strong>Wareneingang {receipt.no}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {dayjs(receipt.date).format("DD.MM.YYYY HH:mm")}
            </Typography.Text>
            {receipt.storageLocation && (
              <>
                <br />
                <Tag color="blue">
                  <Icon icon="fa:warehouse" /> {receipt.storageLocation}
                </Tag>
              </>
            )}
            {relatedReceivings.length > 0 && (
              <div style={{ marginTop: 8, marginLeft: 8 }}>
                {relatedReceivings.map((receiving) => (
                  <div
                    key={receiving.id}
                    style={{
                      borderLeft: "2px solid #d9d9d9",
                      paddingLeft: 8,
                      marginBottom: 4,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }}>
                      <Icon icon="fa:box" /> Prüfung {receiving.no}
                    </Typography.Text>
                    {receiving.date && (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 11, display: "block" }}
                      >
                        {dayjs(receiving.date).format("DD.MM.YYYY")}
                      </Typography.Text>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ),
      });
    });

    // Packages
    packages.forEach((pkg) => {
      items.push({
        color: "orange",
        dot: <Icon icon="fa:box" />,
        children: (
          <>
            <Typography.Text strong>Packstück {pkg.no}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {pkg.packagingDate
                ? dayjs(pkg.packagingDate).format("DD.MM.YYYY HH:mm")
                : "-"}
            </Typography.Text>
            {pkg.packagingType && (
              <>
                <br />
                <Tag color="purple">{pkg.packagingType}</Tag>
              </>
            )}
          </>
        ),
      });
    });

    // DeliveryNotes
    deliveryNotes.forEach((note) => {
      items.push({
        color: "cyan",
        dot: <Icon icon="fa:file-alt" />,
        children: (
          <>
            <Typography.Text strong>Lieferschein {note.no}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {note.date ? dayjs(note.date).format("DD.MM.YYYY HH:mm") : "-"}
            </Typography.Text>
            {note.transportType && (
              <>
                <br />
                <Tag color="blue">
                  <Icon icon="fa:truck" /> {note.transportType}
                </Tag>
              </>
            )}
          </>
        ),
      });
    });

    // Appointed
    if (order.appointedAt) {
      items.push({
        color: "orange",
        dot: <Icon icon="fa:calendar" />,
        children: (
          <>
            <Typography.Text strong>Terminiert</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {dayjs(order.appointedAt).format("DD.MM.YYYY HH:mm")}
            </Typography.Text>
          </>
        ),
      });
    }

    // Finished
    if (order.finishedAt) {
      items.push({
        color: "gray",
        dot: <Icon icon="fa:check-circle" />,
        children: (
          <>
            <Typography.Text strong>Abgeschlossen</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {dayjs(order.finishedAt).format("DD.MM.YYYY HH:mm")}
            </Typography.Text>
          </>
        ),
      });
    }

    return items;
  };

  if (loading) {
    return (
      <AdminLayout>
        <AdminToolbar
          title="Auftragsdetails"
          actions={[
            <Button
              key="back"
              icon={<Icon icon="fa:arrow-left" />}
              onClick={() => navigate("/orders")}
            >
              Zurück
            </Button>,
          ]}
        />
        <div
          className="od-page-main"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <AdminToolbar
          title="Auftragsdetails"
          actions={[
            <Button
              key="back"
              icon={<Icon icon="fa:arrow-left" />}
              onClick={() => navigate("/orders")}
            >
              Zurück
            </Button>,
          ]}
        />
        <div className="od-page-main">
          <Empty
            description={"Auftrag nicht gefunden"}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminToolbar
        title={`Auftrag: ${order.no}`}
        description={order.tenant?.get("label") || undefined}
        actions={[
          <Button
            key="back"
            icon={<Icon icon="fa:arrow-left" />}
            onClick={() => navigate("/orders")}
            color="primary"
            variant="dashed"
          >
            Zurück
          </Button>,
          ...(navList.length > 0
            ? [
                <Button
                  key="prev"
                  icon={<Icon icon="fa:chevron-left" />}
                  disabled={!prevOrder}
                  onClick={() =>
                    prevOrder &&
                    navigate(
                      "/orders/details/" +
                        prevOrder.id +
                        "?hideSourcePicker=true",
                    )
                  }
                >
                  {prevOrder?.no}
                </Button>,
                <Button
                  key="next"
                  icon={<Icon icon="fa:chevron-right" />}
                  iconPosition="end"
                  disabled={!nextOrder}
                  onClick={() =>
                    nextOrder &&
                    navigate(
                      "/orders/details/" +
                        nextOrder.id +
                        "?hideSourcePicker=true",
                    )
                  }
                >
                  {nextOrder?.no}
                </Button>,
              ]
            : []),
        ]}
      />
      <div className="od-page-main">
        <Row gutter={[24, 24]}>
          {/* Timeline on the left */}
          <Col xs={24} md={8} lg={6}>
            <Card title="Zeitverlauf" size="small">
              <Timeline items={getTimelineItems()} />
            </Card>
          </Col>

          {/* Order details and tabs on the right */}
          <Col xs={24} md={16} lg={18}>
            <Row gutter={[16, 16]}>
              {/* Order Details Card */}
              <Col span={24}>
                <Card title="Auftragsdetails" size="small">
                  <Descriptions
                    column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
                    bordered
                  >
                    <Descriptions.Item label="Auftragsnummer">
                      {order.no}
                    </Descriptions.Item>
                    <Descriptions.Item label="Typ">
                      {order.type || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Kommission">
                      {order.commission || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Kostenstelle">
                      {order.cost_center || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Land">
                      {order.country || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="E-Mail">
                      {order.email || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gestartet am">
                      {order.startedAt
                        ? dayjs(order.startedAt).format("DD.MM.YYYY")
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Terminiert für">
                      {order.appointedAt
                        ? dayjs(order.appointedAt).format("DD.MM.YYYY")
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Abgeschlossen am">
                      {order.finishedAt
                        ? dayjs(order.finishedAt).format("DD.MM.YYYY")
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bearbeiter">
                      {order.processor ? getUsername(order.processor) : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verantwortlich">
                      {order.responsible ? getUsername(order.responsible) : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Beschreibung">
                      {order.description || "-"}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Tabbed Content for Features */}
              <Col span={24}>
                <Card size="small">
                  <Tabs
                    items={[
                      {
                        key: "goodsReceiving",
                        label: (
                          <>
                            <Icon icon="fa:box" style={{ marginRight: 6 }} />
                            Warenannahmen
                            <Badge
                              count={goodsReceivings.length}
                              style={{ marginLeft: 8 }}
                            />
                          </>
                        ),
                        children: (
                          <div style={{ marginTop: 16 }}>
                            <GoodsReceivingTable
                              goodsReceivings={goodsReceivings}
                              onViewItems={(receiving) => {
                                setSelectedGoodsReceiving(receiving);
                                setGoodsReceivingDetailsOpen(true);
                              }}
                            />
                          </div>
                        ),
                      },
                      {
                        key: "goodsReceiptProcess",
                        label: (
                          <>
                            <Icon icon="fa:truck" style={{ marginRight: 6 }} />
                            Wareneingänge
                            <Badge
                              count={goodsReceipts.length}
                              style={{ marginLeft: 8 }}
                            />
                          </>
                        ),
                        children: (
                          <div style={{ marginTop: 16 }}>
                            <GoodsReceiptProcessTable
                              goodsReceipts={goodsReceipts}
                              onViewPositions={(receipt) => {
                                setSelectedGoodsReceipt(receipt);
                                setGoodsReceiptDetailsOpen(true);
                              }}
                            />
                          </div>
                        ),
                      },
                      {
                        key: "packages",
                        label: (
                          <>
                            <Icon icon="fa:cube" style={{ marginRight: 6 }} />
                            Packstücke
                            <Badge
                              count={packages.length}
                              style={{ marginLeft: 8 }}
                            />
                          </>
                        ),
                        children: (
                          <div style={{ marginTop: 16 }}>
                            <PackagesTable
                              packages={packages}
                              onViewDetails={(pkg) => {
                                setSelectedPackage(pkg);
                                setPackageDetailsOpen(true);
                              }}
                              // onViewItems={(pkg) => {
                              //   setSelectedPackage(pkg);
                              //   setPackageDetailsOpen(true);
                              // }}
                            />
                          </div>
                        ),
                      },
                      {
                        key: "deliveryNotes",
                        label: (
                          <>
                            <Icon
                              icon="fa:file-alt"
                              style={{ marginRight: 6 }}
                            />
                            Lieferscheine
                            <Badge
                              count={deliveryNotes.length}
                              style={{ marginLeft: 8 }}
                            />
                          </>
                        ),
                        children: (
                          <div style={{ marginTop: 16 }}>
                            <DeliveryNotesTable
                              deliveryNotes={deliveryNotes}
                              onViewDetails={(note) => {
                                setSelectedDeliveryNote(note);
                                setDeliveryNoteDetailsOpen(true);
                              }}
                            />
                          </div>
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Drawers */}
      <GoodsReceivingItemsDrawer
        goodsReceiving={selectedGoodsReceiving}
        open={goodsReceivingDetailsOpen}
        onClose={() => {
          setGoodsReceivingDetailsOpen(false);
          setSelectedGoodsReceiving(null);
        }}
      />

      <GoodsReceiptProcessPositionsDrawer
        goodsReceiptProcess={selectedGoodsReceipt}
        open={goodsReceiptDetailsOpen}
        onClose={() => {
          setGoodsReceiptDetailsOpen(false);
          setSelectedGoodsReceipt(null);
        }}
      />

      <PackageDetailDrawer
        package={selectedPackage}
        open={packageDetailsOpen}
        onClose={() => {
          setPackageDetailsOpen(false);
          setSelectedPackage(null);
        }}
      />

      <DeliveryNoteDetailDrawer
        deliveryNote={selectedDeliveryNote}
        open={deliveryNoteDetailsOpen}
        onClose={() => {
          setDeliveryNoteDetailsOpen(false);
          setSelectedDeliveryNote(null);
        }}
      />
    </AdminLayout>
  );
});
